import { createSlice } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

import { getDeviceOrdersAPI } from '@/api/deviceOrderApi'
import queries from '@/api/queries'
import { queryClient } from '@/queryClient'
import { numberFormat } from '@/util/formatters/numbers'
import { getFilteredList, getRegexSafeString } from '@/util/misc'
import { DEVICE_ORDER_STATUS, getDeviceOrderStatusTextId } from '@/util/orders'
import { getFromLocalStorage } from '@/util/persistance'

import { getMessage } from './i18nReducer'

export const NAME = 'device-orders'

const LIST_ROW_RENDER_STEP = 30

export const createDeviceOrder = ({ id, name, model, ...restOfProps }: any) => ({
  id,
  name,
  model,
  ...restOfProps,
})
// @ts-ignore
export const fetchDeviceOrders = () => async (dispatch, getState) => {
  const isPending = getIsPending(getState())

  if (isPending) return

  dispatch(pending())

  try {
    const orders = await getDeviceOrdersAPI()
    // @ts-ignore
    const newDeviceOrderAmount = orders.filter(order =>
      [DEVICE_ORDER_STATUS.AT_VENDOR, DEVICE_ORDER_STATUS.PROCESSED].includes(order.status)
    ).length

    dispatch(success(orders))
    const currentNotifications = queryClient.getQueryData(queries.notifications())
    queryClient.setQueryData(queries.notifications(), {
      // @ts-ignore
      ...currentNotifications,
      newDeviceOrderAmount,
    })
  } catch (error) {
    dispatch(failure('Could not load your orders'))
  }
}

const initialState = {
  pending: false,
  isDataLoaded: false,
  error: undefined,
  orders: [],
  listRenderLimit: 40,
  statusFilter: DEVICE_ORDER_STATUS.OPEN,
  textFilter: '',
}

const { actions, reducer } = createSlice({
  name: NAME,
  initialState,
  reducers: {
    pending: state => {
      state.error = undefined
      state.pending = true
    },
    success: (state, { payload: orders }) => {
      state.pending = false
      state.isDataLoaded = true
      state.orders = orders
    },
    failure: (state, { payload: error }) => {
      state.pending = false
      state.isDataLoaded = true
      state.error = error
    },
    setTextFilter: (state, { payload: textFilter }) => {
      state.textFilter = textFilter
      state.listRenderLimit = initialState.listRenderLimit
    },
    increaseListRenderLimit: state => {
      state.listRenderLimit = state.listRenderLimit + LIST_ROW_RENDER_STEP
    },
    setStatusFilter: (state, { payload: statusFilter }) => {
      state.statusFilter = statusFilter
      state.listRenderLimit = initialState.listRenderLimit
    },
    reset: () => ({
      ...initialState,
    }),
  },
})

export const {
  pending,
  success,
  failure,
  setTextFilter,
  setStatusFilter,
  increaseListRenderLimit,
  reset,
} = actions
export default reducer
// @ts-ignore
const getState = state => state[NAME]
// @ts-ignore
export const getIsPending = state => getState(state).pending
// @ts-ignore
export const getError = state => getState(state).error
// @ts-ignore
export const getIsDataLoaded = state => getState(state).isDataLoaded
// @ts-ignore
export const getOrders = state => {
  // Todo: remove this when refactored - passing white label in args seems too complicated here
  const whiteLabel = getFromLocalStorage('org')
  // @ts-ignore
  return getState(state).orders.map(order => ({
    ...order,
    statusText: getMessage(state, getDeviceOrderStatusTextId(order.status, whiteLabel)),
  }))
}
// @ts-ignore
export const getFilteredOrders = state =>
  getFilteredList(state, getOrders, textFilter, statusFilter)
// @ts-ignore
export const getCsvExportData = state =>
  getFilteredOrders(state).map(order => ({
    id: order.id,
    customer: order.customer_name,
    user: order.name,
    'phone number': order.code,
    status: order.statusText,
    'down payments': numberFormat(order.remainder_amount === 0 ? 0 : order.down_payments, 0),
    'payment size': numberFormat(
      order.remainder_amount === 0 ? 0 : order.remainder_amount / order.down_payments,
      2
    ),
    cost: numberFormat(order.total_amount, 2),
    'remainder amount': numberFormat(order.remainder_amount, 2),
    'order date': order.order_date ? dayjs(order.order_date).format('YYYY-MM-DD HH:mm') : '',
    'delivery date':
      order.delivery_date || order.estimated_delivery
        ? dayjs(order.delivery_date || order.estimated_delivery).format('YYYY-MM-DD HH:mm')
        : '',
  }))
// @ts-ignore
export const getListRenderLimit = state => getState(state).listRenderLimit
// @ts-ignore
export const getNumberOfOrders = state => getFilteredOrders(state).length
// @ts-ignore
export const getVisibleOrders = state =>
  getFilteredOrders(state).slice(0, getListRenderLimit(state))
// @ts-ignore
export const getOrderListExceedsLimit = state =>
  getListRenderLimit(state) < getNumberOfOrders(state)
// @ts-ignore
export const getTextFilter = state => getState(state).textFilter
// @ts-ignore
export const getStatusFilter = state => getState(state).statusFilter
// @ts-ignore
const textFilter = (state, orders) => {
  const textFilter = getTextFilter(state)
  if (!textFilter) return orders

  const keyWords = getRegexSafeString(textFilter).split(' ')
  // @ts-ignore
  return orders.filter(order =>
    // @ts-ignore
    keyWords.every(keyWord => {
      const regExp = new RegExp(keyWord, 'i')
      return `${order.id}${order.name}${order.code}${order.model}${order.statusText}${
        order.customer_name
      }${
        // @ts-ignore
        order.products?.length > 0 && order.products?.reduce((acc, cur) => acc + cur.model)
      }`.match(regExp)
    })
  )
}
// @ts-ignore
const statusFilter = (state, orders) => {
  const statusFilter = getStatusFilter(state)
  return statusFilter !== DEVICE_ORDER_STATUS.ALL
    ? orders.filter(
        // @ts-ignore
        order =>
          order.status === statusFilter ||
          (statusFilter === DEVICE_ORDER_STATUS.OPEN && orderIsOpen(order.status))
      )
    : orders
}
// @ts-ignore
const orderIsOpen = status =>
  [
    DEVICE_ORDER_STATUS.AT_VENDOR,
    DEVICE_ORDER_STATUS.PROCESSED,
    DEVICE_ORDER_STATUS.SENT_TO_ECOM,
  ].includes(status)
// @ts-ignore
export const getStatusCounter = state =>
  getOrders(state).reduce(
    // @ts-ignore
    (acc, cur) => {
      acc[DEVICE_ORDER_STATUS.ALL]++
      acc[DEVICE_ORDER_STATUS.OPEN] += orderIsOpen(cur.status) ? 1 : 0
      acc[cur.status] = acc[cur.status] ? acc[cur.status] + 1 : 1
      return acc
    },
    {
      [DEVICE_ORDER_STATUS.ALL]: 0,
      [DEVICE_ORDER_STATUS.OPEN]: 0,
    }
  )
